import { useCallback, useEffect, useRef, useState } from "react";
import Logo from "@/components/tpr/Logo/Logo";
import MembershipTermsContent from "@/components/tpr/MembershipTerms/MembershipTermsContent";
import RandomWallpaper from "@/components/tpr/RandomWallpaper/RandomWallpaper";
import TprIcon from "@/components/tpr/TprIcon/TprIcon";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { tprStorage } from "@/core/storage";
import { useLogout } from "@/hooks/useLogout";
import { MemberService } from "@/services/MemberService";
import type { Member } from "@/types/Member";
import { isScrolledToBottom } from "@/utils/scroll";
import { useNavigate, useSearchParams } from "react-router";

const LoginTerms = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptableUseOpen, setAcceptableUseOpen] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [cookiesPolicyOpen, setCookiesPolicyOpen] = useState(false);
  const navigate = useNavigate();
  const logout = useLogout();

  // Gate the Accept button until the member has scrolled to the end of the terms.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasReadTerms, setHasReadTerms] = useState(false);

  const checkScrolledToBottom = useCallback(() => {
    const el = scrollRef.current;
    // Latch on: once they've reached the bottom, keep Accept enabled even if they scroll back up.
    if (el && isScrolledToBottom(el)) setHasReadTerms(true);
  }, []);

  // Re-check on mount and on any reflow (font load / viewport resize) so terms short enough not to
  // scroll enable Accept immediately rather than trapping the member.
  useEffect(() => {
    checkScrolledToBottom();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => checkScrolledToBottom());
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkScrolledToBottom]);

  const termsVersion = parseInt(searchParams.get("v") || "1", 10);
  const nextParam = searchParams.get("next");
  const nextPath = nextParam === "/login/declaration" ? "/login/declaration" : "/dashboard";

  const handleAccept = async () => {
    setLoading(true);
    setError(null);

    try {
      const member = tprStorage.get<Member>("tpruser");
      if (!member) {
        setError("Session expired. Please log in again.");
        setLoading(false);
        return;
      }

      const memberService = new MemberService();
      const result = await memberService.acceptTerms(member.id, termsVersion);

      if (result instanceof Error) {
        setError("Failed to accept terms. Please try again.");
        setLoading(false);
        return;
      }

      // Update stored member with the new termsVersion and mark terms accepted, preserving the
      // annual-declaration acceptance + current terms version the TPR route guard relies on.
      tprStorage.set("tpruser", {
        ...result.member,
        termsAccepted: true,
        annualDeclarationAccepted: member.annualDeclarationAccepted,
        currentTermsVersion: member.currentTermsVersion ?? termsVersion,
      });

      // Redirect to dashboard (or the chained next gate)
      navigate(nextPath, { replace: true });
    } catch {
      setError("An unexpected error occurred. Please try again.");
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    await logout();
  };

  const renderAcceptableUseContent = () => (
    <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
      <p>These are not the terms or conditions that apply to our products.</p>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">General</h4>
        <ol className="list-decimal list-inside space-y-2">
          <li>The terms "we", "us", or "our", refer to The Practice Rooms Ltd.</li>
          <li>The Practice Rooms Ltd is registered at Suite 8, Westbury Court, Church Road, Bristol, BS9 3EF, company registration number 7311013.</li>
          <li>These terms and conditions have no fixed duration.</li>
        </ol>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Accessing our site</h4>
        <ol className="list-decimal list-inside space-y-2">
          <li>Access to our site is permitted on a temporary basis, and we reserve the right to withdraw, suspend, amend the service we provide on our site without notice or close it indefinitely.</li>
          <li>From time to time, we may restrict access to some parts of our site, or our entire site, to users who have registered with us.</li>
          <li>If you choose, or you are provided with, a user identification code, password or any other piece of information as part of our security procedures, you must treat such information as confidential.</li>
          <li>You are responsible for making all arrangements necessary for you to have access to our site.</li>
          <li>We process information about you in accordance with our Privacy Policy.</li>
          <li>We aim to update our site regularly, and may change the content at any time.</li>
          <li>We try to meet highest standards of accessibility, but cannot guarantee that this site will be compatible with all hardware and software.</li>
        </ol>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Materials</h4>
        <ol className="list-decimal list-inside space-y-2">
          <li>All copyrights, trademarks, patents, design rights, and other intellectual property rights relating to our site and the materials published on it will be and remain the sole property of us or our licensors.</li>
          <li>You may view, use, download, print off one copy and store the material on this site for personal use only. Commercial use is not permitted unless you have obtained a licence.</li>
          <li>Our status as the authors of material on our site must always be acknowledged.</li>
          <li>If you print off, copy or download any part of our site in breach of these terms of use, your right to use our site will cease immediately.</li>
          <li>The information in this website is given in good faith and for general information and interest only.</li>
        </ol>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Our Liability</h4>
        <ol className="list-decimal list-inside space-y-2">
          <li>Users of this site are responsible for any decisions they take based on information provided on the site.</li>
          <li>Our sites may contain links to other sites, which are provided for your information only. We accept no responsibility for the content of those sites.</li>
          <li>The material displayed on our site is provided without any representations, guarantees, conditions or warranties as to its accuracy.</li>
          <li>These terms do not exclude our liability for personal injury or death resulting from our negligence, or for fraud.</li>
          <li>Arrangements made between you and any third party named on the site are entirely at your sole risk and responsibility.</li>
        </ol>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Your Liability</h4>
        <ol className="list-decimal list-inside space-y-2">
          <li>You shall compensate us in full if we incur any losses or if any claims or legal proceedings are brought against us arising from your use of our website.</li>
          <li>You agree to indemnify and hold us harmless from any liability, loss, claim, and expense related to any breach by you of these terms.</li>
        </ol>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Uploading material</h4>
        <ol className="list-decimal list-inside space-y-2">
          <li>Whenever you make use of a feature that allows you to upload material to our site, you must comply with the content standards set out in our acceptable use policy.</li>
          <li>We will not be responsible for the content or accuracy of any materials posted by you or any other user of our site.</li>
          <li>We have the right to remove any material or posting you make on our site if it does not comply with our content standards.</li>
          <li>If you upload any material that breaches intellectual property or privacy rights, we have the right to disclose your identity.</li>
        </ol>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Viruses, hacking &amp; other offences</h4>
        <ol className="list-decimal list-inside space-y-2">
          <li>You must not misuse our site by knowingly introducing viruses, Trojans, worms, logic bombs or other malicious material.</li>
          <li>By breaching this provision, you would commit a criminal offence under the Computer Misuse Act 1990.</li>
          <li>We will not be liable for any loss or damage caused by malicious material that may infect your computer equipment.</li>
        </ol>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Jurisdiction and applicable law</h4>
        <ol className="list-decimal list-inside space-y-2">
          <li>The English courts will have exclusive jurisdiction over any claim arising from, or related to, a visit to our site.</li>
          <li>These terms of use shall be governed by and construed in accordance with the law of England and Wales.</li>
        </ol>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Variations</h4>
        <p>We reserve the right to change these terms and conditions from time to time, and the amended terms will be posted on this website.</p>
      </div>

      <p className="text-xs text-gray-500">Last updated April 2025.</p>
    </div>
  );

  const renderPrivacyPolicyContent = () => (
    <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
      <p>This privacy notice tells you what to expect us to do with your personal information.</p>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Contact details</h4>
        <p>The Practice Rooms Ltd. Suite 8, Westbury Court, Church Road, Bristol, BS9 3EF</p>
        <p>info[at]thepracticerooms.co.uk</p>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">What information we collect, use, and why</h4>
        <p className="font-medium">For the operation of client or customer accounts:</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Names and contact details</li>
          <li>Addresses</li>
          <li>Purchase or service history</li>
          <li>Account information, including registration details</li>
          <li>Marketing preferences</li>
          <li>Technical data, including information about browser and operating systems</li>
        </ul>

        <p className="font-medium mt-3">To comply with legal requirements:</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Name</li>
          <li>Contact information</li>
          <li>Identification documents</li>
          <li>Client account information</li>
        </ul>

        <p className="font-medium mt-3">For dealing with queries, complaints or claims:</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Names and contact details</li>
          <li>Address</li>
          <li>Account information</li>
          <li>Purchase or service history</li>
          <li>Financial transaction information</li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Your data protection rights</h4>
        <ul className="list-disc list-inside space-y-2">
          <li><strong>Right of access</strong> - You can ask for copies of your personal information.</li>
          <li><strong>Right to rectification</strong> - You can ask us to correct inaccurate information.</li>
          <li><strong>Right to erasure</strong> - You can ask us to delete your personal information.</li>
          <li><strong>Right to restriction</strong> - You can ask us to limit how we use your information.</li>
          <li><strong>Right to object</strong> - You can object to the processing of your personal data.</li>
          <li><strong>Right to data portability</strong> - You can ask us to transfer your information.</li>
          <li><strong>Right to withdraw consent</strong> - You can withdraw your consent at any time.</li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Lawful bases</h4>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Contract</strong> - we collect information to carry out a contract with you.</li>
          <li><strong>Legal obligation</strong> - we collect information to comply with the law.</li>
          <li><strong>Consent</strong> - we have permission from you after providing relevant information.</li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Where we get personal information from</h4>
        <p>Directly from you.</p>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">How long we keep information</h4>
        <ul className="list-disc list-inside space-y-1">
          <li>Customer account information: 6 years after account closure</li>
          <li>Identity &amp; accreditation documentation: Immediately verified</li>
          <li>Marketing preferences: Until consent withdrawn or 2 years</li>
          <li>Audit logs &amp; system access records: 1 year</li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Who we share information with</h4>
        <ul className="list-disc list-inside space-y-1">
          <li>Financial or fraud investigation authorities</li>
          <li>Regulatory authorities</li>
          <li>Organisations we're legally obliged to share with</li>
          <li>Google, Netlify &amp; Salesforce (anonymised analytical data)</li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">How to complain</h4>
        <p>Contact us using the details above, or complain to the ICO:</p>
        <p className="mt-2">Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF</p>
        <p>Helpline: 0303 123 1113</p>
      </div>
    </div>
  );

  const renderCookiesPolicyContent = () => (
    <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
      <p>We ("The Practice Rooms") use small files (known as Cookies) to see how you use our site and products. We put them on your computer when you visit our site.</p>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Types of Cookies we use</h4>
        <ul className="list-disc list-inside space-y-2">
          <li><strong>Strictly Necessary or Essential Cookies</strong> - for our website to work. They let us save your cookie choice and keep the site secure. They're always on.</li>
          <li><strong>Functional Cookies</strong> - give you a better browsing experience, like showing the site in your preferred language.</li>
          <li><strong>Analytical or Performance Cookies</strong> - help us make our products better by showing us how you use them.</li>
          <li><strong>Marketing Cookies</strong> - make our marketing more efficient, helping us spend less on advertising.</li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">How to block Cookies</h4>
        <p>You can choose to block all or some Cookies through your browser settings. However, if you block all Cookies (including essential ones) you may not be able to use some of the site.</p>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">How to control Cookies</h4>
        <ul className="list-disc list-inside space-y-1">
          <li>Read information on Cookies at AboutCookies.org and AllAboutCookies.org</li>
          <li>Use the European Interactive Digital Advertising Alliance website to decline Cookies across advertising networks</li>
          <li>Use your browser's "Do Not Track" feature</li>
          <li>Install privacy-friendly browsers like Mozilla Firefox or Brave</li>
          <li>Install anti-tracking plug-ins like uBlock, Ghostery or PrivacyBadger</li>
          <li>Use privacy-friendly search engines like DuckDuckGo or Qwant</li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Cookies we use</h4>
        <div className="space-y-3">
          <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded">
            <p className="font-medium">Google Analytics</p>
            <p className="text-xs">Cookies: _ga, _gat, _gid, and others</p>
            <p className="text-xs">Purpose: Analytical information on how visitors use our site</p>
            <p className="text-xs">Lifespan: 1 year</p>
          </div>
          <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded">
            <p className="font-medium">The Practice Rooms - Consent</p>
            <p className="text-xs">Purpose: Remember your cookie preferences</p>
            <p className="text-xs">Lifespan: 1 year</p>
          </div>
          <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded">
            <p className="font-medium">The Practice Rooms - Session</p>
            <p className="text-xs">Purpose: Enable login and save application state</p>
            <p className="text-xs">Lifespan: 1 year</p>
          </div>
        </div>
      </div>

      <p>If you have any questions about the Cookies in use on our website, please contact us.</p>
    </div>
  );

  const renderTermsContent = () => (
    <MembershipTermsContent
      onAcceptableUse={() => setAcceptableUseOpen(true)}
      onPrivacyPolicy={() => setPrivacyPolicyOpen(true)}
      onCookiesPolicy={() => setCookiesPolicyOpen(true)}
    />
  );

  return (
    <div className="bg-white dark:bg-gray-900">
      <div className="flex justify-center min-h-screen">
        <RandomWallpaper />

        <div className="flex items-center w-full max-w-lg px-6 mx-auto lg:w-3/6 py-8">
          <div className="flex-1">
            {/* Logo */}
            <div className="text-center flex flex-col justify-center items-center">
              <div className="mb-4">
                <TprIcon size="large" />
              </div>
              <h2 className="text-4xl font-bold text-center text-gray-700 dark:text-white">
                <Logo color="black" size="large" />
              </h2>

              <p className="mt-3 text-gray-500 dark:text-gray-300">
                We have updated our terms and conditions. Please review and accept these new terms to continue.
              </p>
            </div>

            {/* Terms Content */}
            <div className="mt-6 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div
                ref={scrollRef}
                onScroll={checkScrolledToBottom}
                data-testid="terms-scroll-region"
                className="h-80 overflow-y-auto p-4"
              >
                {renderTermsContent()}
              </div>
            </div>

            {/* Scroll gate hint — shown until the member reaches the end of the terms */}
            {!hasReadTerms && (
              <p className="mt-2 text-xs text-center text-amber-600 dark:text-amber-400">
                Please scroll to the bottom of the terms to enable the Accept button.
              </p>
            )}

            {/* Error Message */}
            {error && (
              <div
                className="mt-4 p-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
                role="alert"
              >
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleAccept}
                disabled={loading || !hasReadTerms}
                className="flex-1"
                aria-label="Accept terms and conditions"
                title={!hasReadTerms ? "Scroll to the bottom of the terms to continue" : undefined}
              >
                {loading ? <Spinner className="h-5 w-5" /> : "Accept"}
              </Button>
              <Button
                variant="secondary"
                onClick={handleDecline}
                disabled={loading}
                className="flex-1"
                aria-label="Decline terms and return to login"
              >
                Decline
              </Button>
            </div>

            <p className="mt-4 text-xs text-center text-gray-400 dark:text-gray-500">
              By clicking Accept, you agree to our updated terms and conditions.
              If you decline, you will be logged out.
            </p>
          </div>
        </div>
      </div>

      {/* Acceptable Use Policy Modal */}
      <Dialog open={acceptableUseOpen} onOpenChange={setAcceptableUseOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Acceptable Use Policy</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-2">
            {renderAcceptableUseContent()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Modal */}
      <Dialog open={privacyPolicyOpen} onOpenChange={setPrivacyPolicyOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Privacy Policy</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-2">
            {renderPrivacyPolicyContent()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cookies Policy Modal */}
      <Dialog open={cookiesPolicyOpen} onOpenChange={setCookiesPolicyOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Cookies &amp; Similar Technologies Policy</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-2">
            {renderCookiesPolicyContent()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginTerms;
